/**
 * Plays image sequences based on the sequence info.
 *
 * This class is a pure model and only outputs image url's that can then be resolved to images somewhere else.
 */
import util from "@/util"

export default class SequencePlayer {
  private readonly id: string
  public readonly info: any

  private index: number
  private readonly firstIndex: number
  private readonly lastIndex: number

  /**
   *
   * @param id the sequence's id string
   * @param info the info.hjson file describing the sequence
   */
  constructor(id: string, info: any) {
    this.id = id
    this.info = info
    this.firstIndex = SequencePlayer.sequenceIndex(this.info.firstImage)
    this.lastIndex = this.firstIndex + this.info.length
    this.index = 0
  }

  goto(index: number): SequencePlayer {
    this.index = index
    this.index = Math.min(this.index, this.info.length - 1)
    this.index = Math.max(this.index, 0)
    return this
  }

  next(): SequencePlayer {
    this.goto(this.index + 1)
    return this
  }

  prev(): SequencePlayer {
    this.goto(this.index - 1)
    return this
  }

  /**
   * Returns the path of the current image in the sequence.
   */
  get(): string {
    return SequencePlayer.setSequenceIndex(`${util.baseUrl()}/${this.id}/${this.info.firstImage}`, this.getImageIndex())
  }

  getAll(): string[] {
    let paths = []
    for (let i = this.firstIndex; i < this.lastIndex; i++) {
      paths.push(SequencePlayer.setSequenceIndex(`${util.baseUrl()}/${this.id}/${this.info.firstImage}`, i))
    }
    return paths
  }

  getThumb(): string {
    return `${util.baseUrl()}/${this.id}/${this.info.thumb}`
  }

  getCurrentIndex(): number {
    return this.index + 1
  }

  getLength(): number {
    return this.info.length
  }

  isVideo(): boolean {
    return util.isVideo(this.info.firstImage)
  }

  get title() {
    return this.info.title
  }

  private getImageIndex(): number {
    return this.firstIndex + this.index
  }

  /** Everything before the index numbers. */
  private static prefix(s: string, regex: RegExp): string {
    let matches = s.match(regex)
    let match = matches?.[matches.length - 1]
    if (match != null) {
      let i = s.lastIndexOf(match)
      return s.substr(0, i)
    } else {
      return s
    }
  }

  /** Everything before the index numbers. */
  private static sequencePrefix(path: string): string {
    let indexRemoved = this.prefix(path, /[0-9]*\./g)
    return indexRemoved
  }

  /** What sequence filetype this path points to, including the dot. (.png, .jpg) */
  private static sequenceSuffix(path: string): string {
    let i = path.lastIndexOf(".")
    return path.substr(i, path.length)
  }

  /** Which image index this path is referring to. */
  private static sequenceIndex(path: string): number {
    let matches = path.match(/([0-9]*)\./g)
    let match = matches?.[matches.length - 1]
    if (match == ".") match = "0"
    return parseFloat(match?.toString() || "0")
  }

  /** How many numbers are used for the index. (img-01 is 2, img-0001 is 4) */
  private static sequenceIndexLength(path: string): number {
    let matches = path.match(/([0-9]*)\./g)
    let match = matches?.[matches.length - 1]
    let l = match?.length
    return l ? l - 1 : 0
  }

  private static setSequenceIndex(path: string, index: number): string {
    let prefix = this.sequencePrefix(path)
    let suffix = this.sequenceSuffix(path)
    let format = this.sequenceIndexLength(path)

    let indexString = ""
    if (format > 0) indexString = index.toString().padStart(format, '0')

    return prefix + indexString + suffix
  }
}
